import { ComponentBase } from "../../core/ComponentBase.js";
import { WebAudioClient } from "../../core/WebAudioClient.js";
import { marked } from "https://cdn.jsdelivr.net/npm/marked/lib/marked.esm.js";
import { AgentFilesPanel } from "./AgentFilesPanel.js";
import { FilePreviewModal } from "./FilePreviewModal.js";
import { DirectToolGroup } from "./DirectToolGroup.js";
import { OutputPresentationCard } from "./OutputPresentationCard.js";
import { ChatMessageList } from "./ChatMessageList.js";
import { ChatSearchOverlay } from "./ChatSearchOverlay.js";
import { ThreadInfoPanel } from "./ThreadInfoPanel.js";
import { ContactPicker } from "./ContactPicker.js";
import { avatarHelper } from "../../helpers/avatarHelper.js";

function _avatarColor(str) {
    const p=['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#06B6D4','#F97316','#14B8A6','#EC4899','#6366F1'];
    let h=0; for(let i=0;i<(str||'').length;i++) h=(h*31+str.charCodeAt(i))|0;
    return p[Math.abs(h)%p.length];
}
function _initials(name) {
    const p=(name||'?').trim().split(/\s+/);
    return p.length>=2?(p[0][0]+p[p.length-1][0]).toUpperCase():(name||'?').slice(0,2).toUpperCase();
}

export class WorkspaceChatArea extends ComponentBase {
    static styles = `
        .ws-chat-area { display: flex; flex-direction: column; height: 100%; position: relative; overflow: hidden; }
        
        /* --- Header --- */
        .ws-chat-header {
            padding: 0.5rem 0.75rem;
            display: flex; justify-content: space-between; align-items: center;
            border-bottom: 1px solid var(--bs-border-color-translucent);
            background-color: rgba(var(--bs-body-bg-rgb), 0.9);
            backdrop-filter: blur(10px); z-index: var(--ab-z-header);
            min-height: 54px; flex-shrink: 0;
        }
        /* Thread identity in header */
        .ws-hdr-identity {
            display: flex; align-items: center; gap: 0.6rem; cursor: pointer;
            border-radius: var(--ab-radius-sm); padding: 0.2rem 0.4rem; transition: background var(--ab-t-fast);
            min-width: 0;
        }
        .ws-hdr-identity:hover { background: var(--ab-hover-bg); }
        .ws-hdr-av {
            width: 36px; height: 36px; border-radius: var(--ab-radius-full); flex-shrink: 0;
            display: flex; align-items: center; justify-content: center;
            font-size: var(--ab-text-xs); font-weight: var(--ab-weight-bold); color: #fff;
        }
        .ws-hdr-av i { font-size: var(--ab-text-md); }
        .ws-hdr-text { min-width: 0; }
        .ws-hdr-name { font-size: var(--ab-text-sm); font-weight: var(--ab-weight-semibold); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ws-hdr-sub  { font-size: var(--ab-text-2xs); color: var(--bs-secondary-color); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        /* Legacy badge (fallback / no-participant mode) */
        .ws-agent-badge {
            display: inline-flex; align-items: center; gap: 0.4rem;
            padding: 0.25rem 0.75rem; border-radius: var(--ab-radius-pill);
            background-color: var(--bs-tertiary-bg);
            border: 1px solid var(--bs-border-color);
            font-size: var(--ab-text-sm); font-weight: var(--ab-weight-medium); color: var(--bs-body-color);
        }
        
        .ws-active-skills-zone {
            position: absolute; top: 50px; left: 0; right: 0; z-index: 5;
            display: flex; flex-direction: column; align-items: center;
            padding: 0.5rem; pointer-events: none; 
        }
        .ws-active-skills-zone > * { pointer-events: auto; box-shadow: var(--bs-box-shadow-lg); }

        .ws-chat-messages {
            flex-grow: 1; overflow-y: auto;
            padding: 1.5rem 1rem 2rem;
            padding-top: 2rem;
        }
        .ws-messages-inner {
            max-width: 760px;
            margin: 0 auto;
            width: 100%;
            display: flex;
            flex-direction: column;
        }

        .ws-scroll-btn {
            position: absolute;
            bottom: 8px; left: 50%; transform: translateX(-50%);
            z-index: 31;
            background: var(--bs-body-bg);
            border: 1px solid var(--bs-border-color);
            color: var(--bs-body-color);
            border-radius: var(--ab-radius-xl);
            padding: 0.3rem 0.9rem;
            font-size: var(--ab-text-sm);
            box-shadow: var(--ab-shadow-sm);
            cursor: pointer;
            display: none;
            align-items: center; gap: 0.35rem;
            transition: opacity 0.2s, bottom 0.2s ease;
            white-space: nowrap;
        }
        .ws-scroll-btn.visible { display: flex; }
        .ws-scroll-btn:hover { background: var(--bs-tertiary-bg); }

        .ws-thinking-bubble {
            position: absolute;
            bottom: 0;
            left: max(1rem, calc(50% - 380px));
            z-index: 20;
            display: none;
            align-items: center; gap: 0.5rem;
            padding: 0.45rem 1rem;
            border-radius: var(--ab-radius-xl);
            background: rgba(var(--bs-body-bg-rgb), 0.85);
            backdrop-filter: blur(10px);
            border: 1px solid var(--bs-border-color-translucent);
            box-shadow: 0 4px 15px rgba(0,0,0,0.12), 0 0 10px rgba(var(--bs-primary-rgb), 0.05);
            animation: fadeIn 0.2s ease-out;
            white-space: nowrap;
            transition: bottom 0.2s ease;
        }
        .ws-thinking-bubble.visible { display: flex; }
        .ws-thinking-dots { display: inline-flex; align-items: center; }
        .ws-thinking-dots span {
            display: inline-block; width: 6px; height: 6px; border-radius: 50%;
            background: var(--bs-primary); margin: 0 2px;
            animation: ws-dot-bounce 1.4s infinite ease-in-out;
            box-shadow: 0 0 4px rgba(var(--bs-primary-rgb), 0.6);
        }
        .ws-thinking-dots span:nth-child(1) { animation-delay: 0s; }
        .ws-thinking-dots span:nth-child(2) { animation-delay: 0.2s; }
        .ws-thinking-dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes ws-dot-bounce {
            0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
            45%            { transform: translateY(-5px); opacity: 1; }
        }

        /* Drag & Drop Overlay styles */
        .ws-drag-overlay {
            position: absolute; inset: 0;
            background: rgba(var(--bs-body-bg-rgb), 0.85);
            backdrop-filter: blur(8px);
            z-index: var(--ab-z-critical);
            display: none;
            flex-direction: column; align-items: center; justify-content: center;
            border: 3px dashed var(--bs-primary);
            border-radius: var(--ab-radius-xl);
            margin: 1rem;
            pointer-events: none;
            animation: fadeIn 0.15s ease-out;
        }
        .ws-drag-overlay.active { display: flex; pointer-events: auto; }
        .ws-drag-icon { font-size: var(--ab-text-lg); color: var(--bs-primary); margin-bottom: 1rem; }
        .ws-drag-text { font-size: var(--ab-text-lg); font-weight: 600; color: var(--bs-body-color); }
        .ws-drag-sub { font-size: var(--ab-text-sm); color: var(--bs-secondary-color); margin-top: 0.25rem; }


        .ws-greeting-container {
            display: flex; flex-direction: column; align-items: center; justify-content: center;
            min-height: 50vh; text-align: center; animation: fadeIn 0.5s ease-out;
            max-width: 600px; margin: 0 auto; padding: 2rem 1rem;
        }
        .ws-greeting-logo {
            width: 64px; height: 64px; border-radius: 50%; background: var(--bs-primary-bg-subtle); color: var(--bs-primary);
            display: flex; align-items: center; justify-content: center; font-size: 2rem; margin-bottom: 1.5rem;
        }
        .ws-greeting-title { font-size: var(--ab-text-lg); font-weight: 600; margin-bottom: 0.5rem; }
        .ws-greeting-subtitle { color: var(--bs-secondary-color); margin-bottom: 2rem; font-size: var(--ab-text-md); }
        .ws-greeting-agent-select { width: 100%; max-width: 350px; text-align: left; }
        .ws-greeting-agent-select .form-select {
            border-radius: var(--ab-radius-lg); padding: 0.75rem 1rem; font-size: var(--ab-text-md); background-color: var(--bs-tertiary-bg);
            border-color: var(--bs-border-color); cursor: pointer; box-shadow: var(--bs-box-shadow-sm);
        }

        .ws-msg-wrapper { display: flex; padding-bottom: 1.5rem; width: 100%; max-width: 100%; animation: fadeIn 0.3s ease-out; }
        .ws-msg-wrapper.user { margin-left: auto; justify-content: flex-end; }
        .ws-msg-wrapper.assistant { margin-right: auto; justify-content: flex-start; flex-direction: column; }
        .ws-msg-wrapper.system { padding: 0 0 1rem 0; margin: 0 auto; justify-content: center; width: 100%; max-width: 600px; }
        .ws-msg-bubble { padding: 0.75rem 1.25rem; border-radius: var(--ab-radius-xl); line-height: 1.5; word-wrap: break-word; max-width: 85%; }
        
        .ws-live-area {
            max-width: 760px;
            margin: 0 auto;
            width: 100%;
            position: relative;
        }
        .ws-msg-wrapper.user .ws-msg-bubble { background-color: var(--bs-secondary-bg); color: var(--bs-body-color); border-radius: var(--ab-radius-xl); border: 1px solid var(--bs-border-color-translucent); }
        .ws-msg-bubble.collapsible { cursor: pointer; }
        .ws-msg-bubble.collapsible.collapsed { max-height: 4.5rem; overflow: hidden; position: relative; }
        .ws-msg-bubble.collapsible.collapsed::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 2rem; background: linear-gradient(transparent, var(--bs-secondary-bg)); border-radius: 0 0 1.5rem 0.5rem; }
        .ws-expand-btn { font-size: var(--ab-text-xs); opacity: 0.8; margin-top: 4px; background: none; border: none; color: rgba(255,255,255,0.85); padding: 0; cursor: pointer; display: block; text-align: right; width: 100%; }
        .ws-msg-wrapper.assistant .ws-msg-bubble { background: none; color: var(--bs-body-color); padding-left: 0.25rem; }
        .ws-msg-bubble pre { background: var(--bs-dark); color: #fff; padding: 1rem; border-radius: var(--ab-radius-md); overflow-x: auto; margin-bottom: 1rem; }
        .ws-msg-bubble table { border-collapse: collapse; width: 100%; margin: 1rem 0; font-size: var(--ab-text-base); }
        .ws-msg-bubble th, .ws-msg-bubble td { border: 1px solid var(--bs-border-color-translucent); padding: 8px 12px; text-align: left; }
        .ws-msg-bubble th { background-color: var(--bs-tertiary-bg); font-weight: 600; }
        .ws-msg-bubble tr:nth-child(even) { background-color: var(--bs-secondary-bg); }
        .ws-msg-bubble :not(pre) > code { background-color: var(--bs-tertiary-bg); padding: 0.15rem 0.3rem; border-radius: var(--ab-radius-xs); font-family: var(--bs-font-monospace); font-size: var(--ab-text-md); color: var(--bs-danger-text-emphasis); }
        .ws-msg-bubble blockquote { border-left: 4px solid var(--bs-primary); padding-left: 1rem; margin-left: 0; color: var(--bs-secondary-color); font-style: italic; margin-bottom: 1rem; }
        .ws-msg-bubble ul, .ws-msg-bubble ol { padding-left: 1.5rem; margin-bottom: 1rem; }
        .ws-msg-bubble li { margin-bottom: 0.25rem; }
        .ws-msg-bubble p { margin-bottom: 1rem; }
        .ws-system-msg { text-align: center; color: var(--bs-secondary-color); font-size: var(--ab-text-sm); font-style: italic; }
        .ws-history-loader { display: flex; align-items: center; justify-content: center; padding: 1rem 0; width: 100%; gap: 0.5rem; }

        .ws-voice-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: var(--bs-body-bg); z-index: 50; display: flex; flex-direction: column; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity 0.3s ease; }
        .ws-voice-overlay.active { opacity: 1; pointer-events: auto; }
        .voice-visualizer { width: 200px; height: 200px; border-radius: 50%; background: var(--bs-tertiary-bg); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 2rem; transition: all 0.3s ease; }
        .voice-visualizer i { font-size: 4rem; color: var(--bs-secondary-color); z-index: 2; transition: color 0.3s; }
        .voice-mode-listening .voice-visualizer { box-shadow: 0 0 0 0 rgba(var(--bs-primary-rgb), 0.7); animation: pulse-ring 2s infinite; }
        .voice-mode-listening .voice-visualizer i { color: var(--bs-primary); }
        .voice-mode-speaking .voice-visualizer { background: var(--bs-primary); animation: breathe 3s infinite ease-in-out; }
        .voice-mode-speaking .voice-visualizer i { color: #fff; }
        .voice-status-text { font-size: var(--ab-text-lg); font-weight: 500; color: var(--bs-heading-color); margin-bottom: 3rem; }
        .voice-controls { display: flex; gap: 1.5rem; }
        .voice-btn { width: 56px; height: 56px; border-radius: 50%; border: none; display: flex; align-items: center; justify-content: center; font-size: var(--ab-text-lg); cursor: pointer; transition: transform 0.1s; }
        .voice-hangup-btn { background: var(--bs-danger); color: #fff; }
        .voice-mute-btn { background: var(--bs-secondary-bg); color: var(--bs-body-color); }
        .voice-mute-btn.muted { background: var(--bs-danger-bg-subtle); color: var(--bs-danger); }
        .ephemeral-transcript { opacity: 0.7; }
        @keyframes pulse-ring { 0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(var(--bs-primary-rgb), 0.7); } 70% { transform: scale(1); box-shadow: 0 0 0 20px rgba(var(--bs-primary-rgb), 0); } 100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(var(--bs-primary-rgb), 0); } }
        @keyframes breathe { 0% { transform: scale(1); } 50% { transform: scale(1.05); } 100% { transform: scale(1); } }
        .ws-jump-to-skill-btn { position: absolute; top: 70px; left: 50%; transform: translateX(-50%); background: var(--bs-primary); color: #fff; border: none; padding: 0.4rem 1rem; border-radius: var(--ab-radius-xl); box-shadow: var(--ab-shadow-md); font-size: var(--ab-text-sm); font-weight: 500; display: none; z-index: 20; cursor: pointer; opacity: 0.9; }
        .ws-jump-to-skill-btn:hover { opacity: 1; transform: translateX(-50%) translateY(-1px); }
        .ws-copy-btn { position: absolute; top: 6px; right: 6px; background: none; border: none; padding: 2px 5px; border-radius: var(--ab-radius-xs); color: var(--bs-secondary-color); cursor: pointer; opacity: 0; transition: opacity 0.15s; font-size: var(--ab-text-sm); line-height: 1; }
        .ws-msg-bubble:hover .ws-copy-btn { opacity: 1; }
        .ws-copy-btn:hover { background: var(--bs-secondary-bg); color: var(--bs-body-color); }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

        .search-highlight,
        .search-highlight .ws-msg-bubble {
            animation: searchHighlightPulse 3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            border-radius: var(--ab-radius-md);
        }
        @keyframes searchHighlightPulse {
            0% { background-color: rgba(var(--bs-primary-rgb), 0.45); }
            100% { background-color: transparent; }
        }
    `;

    constructor(config) {
        super(config);
        this.workspace = config.workspace;
        this.activeSkillCards = new Map();
        this.activeDelegationCards = new Map();
        this.activeDirectTools = new Map();
        this.runningJobIds = new Set();
        this._currentTurnTools = []; // Metadata state to rebuild tool group on commit

        this.isProcessing = false;
        this.isCompacting = false;

        this.isVoiceActive = false;
        this.audioClient = null;
        this.isMuted = false;
        this.filesPanel = null;
        this.previewModal = null;
        this.currentTurnToolGroup = null;
        this._pendingPresentations = [];
        this.chatList = null;

        this._boundScrollHandler = this.checkScrollPosition.bind(this);
        this._boundHandleFilesPanelToggled = this.handleFilesPanelToggled.bind(this);
        this._boundToggleChatSearch = () => {
            if (this.searchOverlay?.isOpen) {
                this.searchOverlay.close();
            } else {
                this.filesPanel?.close();
                this.searchOverlay?.open();
            }
        };
        this._boundHandleChatEvent = this._onChatEvent.bind(this);
        this._boundHandleAgentAvatarUpdated = this._handleAgentAvatarUpdated.bind(this);
        this.applyStyles();
    }

    async initialize() {
        this.appClient.events.local.on('agentFilesPanelToggled', this._boundHandleFilesPanelToggled);
        this.appClient.events.local.on('TOGGLE_CHAT_SEARCH', this._boundToggleChatSearch);
        this.appClient.events.server.on('chatEvent', this._boundHandleChatEvent);
        this.appClient.events.local.on('AGENT_AVATAR_UPDATED', this._boundHandleAgentAvatarUpdated);
        marked.setOptions({ breaks: true, gfm: true });
        await super.initialize();
    }

    async initAfterLogin() {
        await this.loadAvailableAgents();
    }

    async loadAvailableAgents() {
        try {
            const res = await this.appClient.rpcClient.call('AgentConfigRpc.getData', { pageSize: 100 });
            this.agents = res.data || [];
        } catch (e) {
            this.log.warn("Could not load agents.");
        }
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'ws-chat-area';

            this.headerEl = document.createElement('div');
            this.headerEl.className = 'ws-chat-header';
            this.container.appendChild(this.headerEl);

            // Thread info panel (right-sliding, holds participant mgmt)
            this.infoPanel = new ThreadInfoPanel({
                instanceId: `${this.config.instanceId}-info`,
                appClient: this.appClient,
                workspace: this.workspace,
                onAddMember: () => this.showContactPicker('add_member'),
            });
            await this.infoPanel.render(this.container);

            this.stickySkillsContainer = document.createElement('div');
            this.stickySkillsContainer.className = 'ws-active-skills-zone';
            this.container.appendChild(this.stickySkillsContainer);

            this.jumpBtn = document.createElement('button');
            this.jumpBtn.className = 'ws-jump-to-skill-btn';
            this.jumpBtn.innerHTML = '<i class="bi bi-arrow-up-circle-fill me-1"></i> View Running Task';
            this.jumpBtn.addEventListener('click', () => this.scrollToActiveSkill());
            this.container.appendChild(this.jumpBtn);

            this.messagesContainer = document.createElement('div');
            this.messagesContainer.className = 'ws-chat-messages';
            this.messagesContainer.addEventListener('scroll', this._boundScrollHandler);
            // Link clicks are intercepted globally in ApplicationClient.js for all components,
            // including WorkspaceChatArea, DataCrud, and sidebars.

            this.messagesInner = document.createElement('div');
            this.messagesInner.className = 'ws-messages-inner';
            this.messagesContainer.appendChild(this.messagesInner);

            this.liveArea = document.createElement('div');
            this.liveArea.className = 'ws-live-area';
            this.messagesContainer.appendChild(this.liveArea);

            this.container.appendChild(this.messagesContainer);

            this.chatList = new ChatMessageList({
                instanceId: `${this.config.instanceId}-chatlist`,
                appClient: this.appClient,
                workspace: this.workspace,
                scrollEl: this.messagesContainer,
                messagesInner: this.messagesInner,
                liveArea: this.liveArea,
                createManagedComponent: this.createManagedComponent.bind(this),
                activeSkillCards: this.activeSkillCards,
                runningJobIds: this.runningJobIds,
            });

            this.scrollBtn = document.createElement('button');
            this.scrollBtn.className = 'ws-scroll-btn';
            this.scrollBtn.innerHTML = '<i class="bi bi-arrow-down"></i> Scroll to bottom';
            this.scrollBtn.addEventListener('click', () => this.scrollToBottom(true));
            this.container.appendChild(this.scrollBtn);

            this.thinkingBubble = document.createElement('div');
            this.thinkingBubble.className = 'ws-thinking-bubble';
            this.thinkingBubble.innerHTML = `
                <div class="ws-thinking-dots"><span></span><span></span><span></span></div>
                <span class="ws-thinking-text" style="font-size:var(--ab-text-sm);color:var(--bs-secondary-color);font-style:italic;">Working…</span>
            `;
            this.container.appendChild(this.thinkingBubble);

            this.voiceOverlay = document.createElement('div');
            this.voiceOverlay.className = 'ws-voice-overlay';
            this.voiceOverlay.innerHTML = `
                <div class="voice-visualizer"><i class="bi bi-mic-fill"></i></div>
                <div class="voice-status-text">Connecting...</div>
                <div class="voice-controls">
                    <button class="voice-btn voice-mute-btn" id="voice-mute" title="Mute"><i class="bi bi-mic-fill"></i></button>
                    <button class="voice-btn voice-hangup-btn" id="voice-hangup" title="End Call"><i class="bi bi-telephone-x-fill"></i></button>
                </div>
            `;
            this.voiceOverlay.querySelector('#voice-hangup').addEventListener('click', () => this.stopVoiceMode());
            this.voiceOverlay.querySelector('#voice-mute').addEventListener('click', () => this.toggleMute());
            this.container.appendChild(this.voiceOverlay);

            // Drag and drop overlay
            this.dragOverlay = document.createElement('div');
            this.dragOverlay.className = 'ws-drag-overlay';
            this.dragOverlay.innerHTML = `
                <i class="bi bi-cloud-arrow-up-fill ws-drag-icon"></i>
                <div class="ws-drag-text">Drop files or folders here</div>
                <div class="ws-drag-sub">They will be attached to the conversation</div>
            `;
            this.container.appendChild(this.dragOverlay);

            // Drag and drop listeners on container
            this.container.addEventListener('dragenter', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (this.workspace.currentThreadId) {
                    this.dragOverlay.classList.add('active');
                }
            });
            this.dragOverlay.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.stopPropagation();
            });
            this.dragOverlay.addEventListener('dragleave', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.dragOverlay.classList.remove('active');
            });
            this.dragOverlay.addEventListener('drop', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.dragOverlay.classList.remove('active');
                if (!this.workspace.currentThreadId) return;

                const files = await this.getAllFilesFromDataTransfer(e.dataTransfer);
                if (files && files.length > 0) {
                    const inputComponent = this.workspace.inputComponent;
                    if (inputComponent && typeof inputComponent.attachDroppedFiles === 'function') {
                        inputComponent.attachDroppedFiles(files);
                    }
                }
            });


            this.filesPanel = new AgentFilesPanel({ instanceId: `${this.config.instanceId}-files`, appClient: this.appClient, workspace: this.workspace });
            await this.filesPanel.render(this.container);

            this.previewModal = new FilePreviewModal({ instanceId: `${this.config.instanceId}-preview`, appClient: this.appClient, workspace: this.workspace });
            await this.previewModal.initialize();

            this.searchOverlay = new ChatSearchOverlay({ instanceId: `${this.config.instanceId}-search`, appClient: this.appClient, workspace: this.workspace, chatArea: this });
            await this.searchOverlay.initialize();
            await this.searchOverlay.render(this.container);
        }

        if (!this.workspace.currentThreadId) {
            this.renderGreeting();
        } else {
            this.renderHeader();
        }

        return this.container;
    }

    renderHeader() {
        const singleAgentMode = this.workspace.singleAgentMode;
        const enableParticipants = !singleAgentMode && this.workspace.config?.options?.enableUserParticipants !== false;
        const thread = this._currentThread;
        const agentName = this.workspace.currentAgentName || 'Assistant';

        // Determine if the thread has an AI agent (controls files/VNC button visibility)
        const hasAgent = !thread || thread.type === 'ai_only' || !!thread.agentName ||
            (thread.members||[]).some(m => m.participantType === 'agent');

        const toggleBtn = singleAgentMode ? '' : `<button class="ab-icon-btn me-1" id="ws-sidebar-toggle" title="${this.t("Toggle sidebar")}" aria-label="${this.t("Toggle sidebar")}"><i class="bi bi-layout-sidebar"></i></button>`;

        // Build identity section — WhatsApp-style when participant mode is on, legacy badge otherwise
        let identityHtml;
        if (enableParticipants && thread) {
            const members = thread.members || [];
            // Group = explicit group type, OR 3+ members total
            const isGroup = thread.type === 'group' || thread.type === 'ai_human' || members.length > 2;
            const isAgent = !isGroup && (members.length <= 1 || members.some(m => m.participantType === 'agent'));
            const displayName = (isGroup && thread.groupName) ? thread.groupName : thread.title;

            let avatarHtml;
            const myUserId = this.appClient.getSharedData('user')?._id || this.appClient.getSharedData('user')?.id;

            if (isAgent) {
                const agentMember = members.find(m => m.participantType === 'agent');
                let agentAvatar = agentMember?.avatarUrl || thread.avatar || thread.avatarUrl || thread.agentAvatar;
                let agentDisplayName = thread.agentName || (agentMember ? (agentMember.displayName || agentMember.participantId) : null) || displayName || 'Agent';

                const agentName = thread.agentName || agentMember?.participantId;
                if (agentName && this.workspace?.agents) {
                    const matchedAgent = this.workspace.agents.find(a => a.name === agentName);
                    if (matchedAgent) {
                        if (matchedAgent.avatarUrl) {
                            agentAvatar = matchedAgent.avatarUrl;
                        }
                        if (matchedAgent.name) {
                            agentDisplayName = matchedAgent.name;
                        }
                    }
                }

                const avEl = avatarHelper.createAvatarElement({
                    displayName: agentDisplayName,
                    avatarUrl: agentAvatar,
                    type: 'agent',
                    additionalClasses: ['ws-hdr-av'],
                    sizeClass: 'small'
                });
                avatarHtml = avEl.outerHTML;
            } else if (isGroup) {
                const avEl = avatarHelper.createAvatarElement({
                    displayName: displayName || 'Group',
                    avatarUrl: thread.avatar || thread.avatarUrl,
                    type: 'group',
                    additionalClasses: ['ws-hdr-av'],
                    sizeClass: 'small'
                });
                avatarHtml = avEl.outerHTML;
            } else {
                const other = members.find(m => m.participantId !== myUserId);
                const name = other?.displayName || displayName || 'User';
                const avEl = avatarHelper.createAvatarElement({
                    displayName: name,
                    avatarUrl: other?.avatarUrl || thread.avatar || thread.avatarUrl,
                    type: other?.participantType === 'agent' ? 'agent' : 'user',
                    additionalClasses: ['ws-hdr-av'],
                    sizeClass: 'small'
                });
                avatarHtml = avEl.outerHTML;
            }

            let subtitle;
            if (isGroup) {
                // Resolve names: check RDS for users, use displayName for agents
                const rdsMap = this.appClient.rds?.data?.get('users');
                const names = members.slice(0, 3).map(m => {
                    if (m.participantType === 'agent') return m.displayName || m.participantId;
                    const rdsUser = rdsMap?.get(m.participantId);
                    if (rdsUser) return [rdsUser.firstName, rdsUser.lastName].filter(Boolean).join(' ') || rdsUser.username || m.participantId;
                    const me = this.appClient.getSharedData('user');
                    if (me?._id === m.participantId) return [me.firstName, me.lastName].filter(Boolean).join(' ') || me.username || 'You';
                    return m.displayName || `…${m.participantId.slice(-6)}`;
                }).join(', ');
                subtitle = members.length > 3 ? `${names} · ${members.length} members` : names;
            } else {
                subtitle = this.escapeHtml(agentName);
            }

            identityHtml = `
                <div class="ws-hdr-identity" id="hdr-identity">
                    ${avatarHtml}
                    <div class="ws-hdr-text">
                        <div class="ws-hdr-name">${this.escapeHtml(displayName)}</div>
                        <div class="ws-hdr-sub">${subtitle}</div>
                    </div>
                </div>`;

        } else {
            // Legacy badge (no participant mode or no thread)
            identityHtml = `
                <div class="ws-agent-badge">
                    <i class="bi bi-robot text-primary"></i>
                    <span>${this.escapeHtml(agentName)}</span>
                </div>`;
        }

        // Right-side action buttons — files/VNC only shown when AI agent present and sandbox is supported
        const supportsSandbox = this.workspace.isSandboxSupported();
        const aiButtonsHtml = (hasAgent && supportsSandbox) ? `
            <button class="ab-icon-btn me-1" id="view-files-btn" title="${this.t("Agent Files")}" aria-label="${this.t("Agent Files")}"><i class="bi bi-folder2-open"></i></button>
            <button class="ab-icon-btn me-1" id="view-screen-btn" title="${this.t("View Remote Screen")}" aria-label="${this.t("View Remote Screen")}"><i class="bi bi-display"></i></button>` : '';

        // Clear chat button (trash bin) to mimic clear chat behavior in singleAgentMode
        const clearChatBtnHtml = singleAgentMode ? `
            <button class="ab-icon-btn me-1" id="ws-clear-chat-btn" title="${this.t("Clear Chat")}" aria-label="${this.t("Clear Chat")}"><i class="bi bi-trash"></i></button>` : '';

        // Info panel button — only when participant mode is on and a thread is loaded
        const infoBtnHtml = (enableParticipants && thread) ?
            `<button class="ab-icon-btn" id="view-info-btn" title="${this.t("Conversation Info")}" aria-label="${this.t("Conversation Info")}"><i class="bi bi-three-dots-vertical"></i></button>` : '';

        this.headerEl.innerHTML = `
            <div class="d-flex align-items-center" style="min-width:0;flex:1;">
                ${toggleBtn}
                ${identityHtml}
            </div>
            <div class="d-flex gap-2 flex-shrink-0">
                ${aiButtonsHtml}
                ${clearChatBtnHtml}
                ${infoBtnHtml}
            </div>
        `;

        const toggleBtnEl = this.headerEl.querySelector('#ws-sidebar-toggle');
        if (toggleBtnEl) {
            toggleBtnEl.addEventListener('click', () => {
                if (window.innerWidth <= 768) this.workspace.toggleSidebar(true);
                else this.workspace.toggleDesktopSidebar();
            });
        }

        if (singleAgentMode) {
            this.headerEl.querySelector('#ws-clear-chat-btn')?.addEventListener('click', async () => {
                if (await this.appClient.notifier.confirm("Are you sure you want to clear this conversation?")) {
                    await this.clearSingleAgentChat();
                }
            });
        }

        if (enableParticipants && thread) {
            this.headerEl.querySelector('#hdr-identity')?.addEventListener('click', () => {
                this.infoPanel?.toggle(this._currentThread);
            });
            this.headerEl.querySelector('#view-info-btn')?.addEventListener('click', () => {
                this.infoPanel?.toggle(this._currentThread);
            });
        }

        const screenBtn = this.headerEl.querySelector('#view-screen-btn');
        if (screenBtn) {
            screenBtn.addEventListener('click', async () => {
                const agent = this.workspace.agents.find(a => a.name === this.workspace.currentAgentName);
                let identityId = `agent-${this.workspace.currentAgentName}`;
                if (agent?._id) identityId = `agent-${agent.name}-${agent._id}`;
                await this.workspace.ensureThread();
                this.appClient.events.fireLocal('DISPLAY_REMOTE_SCREEN', { identityId, threadId: this.workspace.currentThreadId });
            });
        }

        const filesBtn = this.headerEl.querySelector('#view-files-btn');
        if (filesBtn) {
            filesBtn.addEventListener('click', () => {
                if (!this.workspace.currentThreadId) return;
                if (this.filesPanel?.isOpen) {
                    this.filesPanel.close();
                } else {
                    this.searchOverlay?.close();
                    this.filesPanel?.open(this.workspace.currentThreadId);
                }
            });
        }

        this.workspace.inputComponent?.updateCapabilities?.();
    }

    async clearSingleAgentChat() {
        const threadId = this.workspace.currentThreadId;
        if (!threadId) return;
        try {
            // Delete the thread
            await this.appClient.rpcClient.call('ThreadRpc.delete', { _id: threadId }, { projectId: this.workspace.currentProjectId });

            // Remove from sidebar list
            if (this.workspace.sidebarComponent) {
                this.workspace.sidebarComponent.allThreads = this.workspace.sidebarComponent.allThreads.filter(t => t._id !== threadId);
            }

            // Clear current thread ID
            this.workspace.currentThreadId = null;

            // Re-run thread loading/creation logic
            await this.workspace.loadSingleAgentThread();
        } catch (e) {
            this.workspace.log.error("Failed to clear chat:", e);
            this.appClient.notifier.error("Failed to clear conversation.");
        }
    }

    // ── Contact Picker ────────────────────────────────────────────────────────

    showContactPicker(mode = 'new_chat') {
        this.renderHeader();
        this._clearGreeting();
        if (this.chatList) this.chatList.setMessages([], false);
        if (this.liveArea) this.liveArea.innerHTML = '';
        if (this._contactPicker) { this._contactPicker.unmount(); this._contactPicker = null; }
        // Wipe any stale content from old threads that isn't part of chatList/liveArea/greeting
        if (this.messagesInner) {
            Array.from(this.messagesInner.children).forEach(child => {
                if (!child.classList.contains('ws-chat-list') && !child.classList.contains('ws-live-area')) {
                    child.remove();
                }
            });
        }

        this._contactPicker = new ContactPicker({
            workspace: this.workspace,
            appClient: this.appClient,
            mode,
            onCancel: () => this.hideContactPicker(),
            onSelect: (contact) => this._handleContactSelected(contact, mode),
        });
        this._contactPicker.mount(this.messagesInner);
        this.infoPanel?.close();
    }

    hideContactPicker() {
        if (this._contactPicker) { this._contactPicker.unmount(); this._contactPicker = null; }
        if (this.workspace.currentThreadId) {
            this.loadHistory(this.workspace.currentThreadId);
        } else {
            this.renderGreeting();
        }
    }

    async _handleContactSelected(contact, mode) {
        if (this._contactPicker) { this._contactPicker.unmount(); this._contactPicker = null; }

        if (mode === 'add_member') {
            if (!this.workspace.currentThreadId) { this.renderGreeting(); return; }
            try {
                const thread = this.workspace.sidebarComponent?.allThreads?.find(t => t._id === this.workspace.currentThreadId) || this._currentThread;

                // Guard: already a member?
                const alreadyMember = thread?.members?.some(m => m.participantId === contact.id);
                if (alreadyMember) {
                    this.appClient.notifier.warn(`${contact.displayName} is already in this conversation.`);
                    if (this.workspace.currentThreadId) {
                        this.loadHistory(this.workspace.currentThreadId);
                    } else {
                        this.renderGreeting();
                    }
                    return;
                }

                await this.appClient.rpcClient.call('ThreadRpc.addMember', {
                    threadId: this.workspace.currentThreadId,
                    participantId: contact.id,
                    participantType: contact.type,
                    displayName: contact.displayName,
                    role: 'member',
                }, { projectId: this.workspace.currentProjectId });

                // Update local thread object optimistically
                if (thread) {
                    if (!thread.members) thread.members = [];
                    thread.members.push({
                        participantId: contact.id, participantType: contact.type,
                        displayName: contact.displayName, role: 'member', addedAt: new Date()
                    });
                    this._currentThread = thread; // lock in updated state
                }

                this.appClient.notifier.success(`${contact.displayName} added.`);
                // Reload to show system message, then restore updated thread state
                if (this.workspace.currentThreadId) {
                    await this.loadHistory(this.workspace.currentThreadId);
                    // loadHistory may have loaded stale thread from sidebar cache — restore fresh copy
                    if (thread) {
                        this._currentThread = thread;
                        this.infoPanel?.setThread(thread);
                        this.renderHeader();
                    }
                }
            } catch (e) {
                this.appClient.notifier.error(`Failed to add: ${e.message}`);
                if (this.workspace.currentThreadId) {
                    this.loadHistory(this.workspace.currentThreadId);
                } else {
                    this.renderGreeting();
                }
            }

            return;
        }

        // new_chat: create thread with selected contact
        try {
            const defaultAgent = this.workspace.currentAgentName || this.workspace.agents[0]?.name || 'PrimaryAssistant';
            const isAgent = contact.type === 'agent';
            const threadData = isAgent ? {
                title: `Chat with ${contact.displayName}`,
                agentName: contact.id,
                type: 'ai_only',
            } : {
                title: contact.displayName,
                agentName: defaultAgent,
                type: 'ai_human',
                members: [
                    { participantId: contact.id, participantType: 'user', displayName: contact.displayName, role: 'member' },
                ],
            };
            const newThread = await this.appClient.rpcClient.call(
                'ThreadRpc.create', threadData, { projectId: this.workspace.currentProjectId }
            );
            this.workspace.sidebarComponent?.addThreadOptimistically(newThread);
            await this.workspace.loadThread(newThread._id);
        } catch (e) {
            this.appClient.notifier.error(`Failed to create conversation: ${e.message}`);
            this.renderGreeting();
        }
    }

    async startVoiceMode() {

        this.isVoiceActive = true;
        this.voiceOverlay.classList.add('active');
        this.updateVoiceStatus('Connecting...');

        try {
            if (!this.audioClient) {
                this.audioClient = new WebAudioClient(this.appClient);
                this.audioClient.on('error', (err) => { this.appClient.notifier.error(err); this.stopVoiceMode(); });
            }
            await this.audioClient.start();
            await this.appClient.rpcClient.call('RealtimeAiRpc.startVoiceSession', { agentName: this.workspace.currentAgentName, threadId: this.workspace.currentThreadId }, { projectId: this.workspace.currentProjectId });
            this.updateVoiceStatus('Listening...');
            this.voiceOverlay.classList.add('voice-mode-listening');
        } catch (e) {
            this.appClient.notifier.error(`Failed to start voice mode: ${e.message}`);
            this.stopVoiceMode();
        }
    }

    async stopVoiceMode() {
        this.isVoiceActive = false;
        this.voiceOverlay.classList.remove('active', 'voice-mode-listening', 'voice-mode-speaking');
        if (this.audioClient) { await this.audioClient.stop(); this.audioClient = null; }
        try { await this.appClient.rpcClient.call('RealtimeAiRpc.stopVoiceSession', {}); } catch (e) {}
        setTimeout(async () => { if (this.workspace.currentThreadId) await this.loadHistory(this.workspace.currentThreadId); }, 500);
    }

    toggleMute() {
        this.isMuted = !this.isMuted;
        const btn = this.voiceOverlay.querySelector('#voice-mute');
        if (this.isMuted) { btn.classList.add('muted'); btn.innerHTML = '<i class="bi bi-mic-mute-fill"></i>'; } 
        else { btn.classList.remove('muted'); btn.innerHTML = '<i class="bi bi-mic-fill"></i>'; }
    }

    updateVoiceStatus(text) { this.voiceOverlay.querySelector('.voice-status-text').textContent = text; }

    handleVoiceEvent(status) {
        this.voiceOverlay.classList.remove('voice-mode-listening', 'voice-mode-speaking');
        if (status === 'speaking') { this.voiceOverlay.classList.add('voice-mode-speaking'); this.updateVoiceStatus('Speaking...'); } 
        else if (status === 'listening' || status === 'idle') { this.voiceOverlay.classList.add('voice-mode-listening'); this.updateVoiceStatus('Listening...'); }
    }

    renderGreeting() {
        if (this.workspace.currentThreadId) {
            // Thread is selected but has no messages yet — show a subtle prompt, not the picker
            this._clearGreeting();
            const wrapper = document.createElement('div');
            wrapper.className = 'ws-greeting-container';
            wrapper.innerHTML = `
                <div class="ws-greeting-logo"><i class="bi bi-chat-dots"></i></div>
                <h2 class="ws-greeting-title">Start the conversation</h2>
                <p class="ws-greeting-subtitle">Type your first message below.</p>
            `;
            this.messagesInner.appendChild(wrapper);
        } else {
            // No thread selected — show the contact/agent picker
            this.showContactPicker('new_chat');
        }
    }

    _clearGreeting() {
        const greeting = this.messagesInner?.querySelector('.ws-greeting-container') || this.liveArea?.querySelector('.ws-greeting-container');
        if (greeting) greeting.remove();
    }

    setAgent(agentName) { this.renderHeader(); }

    clearChat() {
        this.isProcessing = false;
        this.isCompacting = false;
        if (this.thinkingBubble) {
            this.thinkingBubble.classList.remove('visible');
            this.updateThinkingBubbleText(this.t('Working…'), true);
        }
        if (this.chatList) this.chatList.setMessages([]);
        this._clearGreeting();
        this.renderGreeting();
        if (this.stickySkillsContainer) this.stickySkillsContainer.innerHTML = '';
        this.activeSkillCards.clear();
        this.activeDelegationCards.clear();
        this.activeDirectTools.clear();
        this.runningJobIds.clear();
        this._currentTurnTools = [];
        if (this.liveArea) this.liveArea.innerHTML = '';
        if (this.jumpBtn) this.jumpBtn.style.display = 'none';
    }

    async loadHistory(threadId) {
        this.isProcessing = false;
        this.isCompacting = false;
        if (this.thinkingBubble) {
            this.thinkingBubble.classList.remove('visible');
            this.updateThinkingBubbleText(this.t('Working…'), true);
        }
        this.infoPanel?.close();
        this.renderHeader();
        if (this.filesPanel?.isOpen) this.filesPanel.close();
        this._clearGreeting();
        if (this._contactPicker) {
            this._contactPicker.unmount();
            this._contactPicker = null;
        }

        // Resolve thread metadata for header + info panel
        this._currentThread = this.workspace.sidebarComponent?.allThreads?.find(t => t._id === threadId) || null;
        this.infoPanel?.setThread(this._currentThread);
        this.renderHeader(); // re-render with resolved thread

        this.activeSkillCards.clear();
        this.activeDelegationCards.clear();
        this.activeDirectTools.clear();
        this.runningJobIds.clear();
        this._currentTurnTools = [];
        if (this.liveArea) this.liveArea.innerHTML = '';

        try {
            const res = await this.appClient.rpcClient.call('ChatRpc.getThreadMessages', { threadId, limit: 30 }, { projectId: this.workspace.currentProjectId });
            const msgs = res.messages || [];
            
            if (msgs.length === 0) {
                this.renderGreeting();
                if (this.chatList) this.chatList.setMessages([], false);
            } else {
                if (this.chatList) this.chatList.setMessages(msgs, res.hasMore);
                // If a sidebar message search triggered this load, wait for the message list
                // to fully settle (setMessages uses a 250-1200ms scroll-pinning timeout)
                // before jumping to the target message.
                const pendingJump = this.workspace._pendingMessageJump;
                if (pendingJump) {
                    this.workspace._pendingMessageJump = null;
                    const _waitAndJump = async () => {
                        const maxWait = 40; // 40 × 50ms = 2s max
                        for (let i = 0; i < maxWait; i++) {
                            await new Promise(r => setTimeout(r, 50));
                            if (!this.chatList?._isSettingMessages) break;
                        }
                        this.jumpToMessage(pendingJump);
                    };
                    _waitAndJump();
                } else {
                    this.scrollToBottom();
                }
            }
        } catch (e) {
            this.log.error("Failed to load history", e);
        }
    }

    async refreshMessages() {
        const threadId = this.workspace.currentThreadId;
        if (!threadId) return;
        try {
            const res = await this.appClient.rpcClient.call('ChatRpc.getThreadMessages', { threadId, limit: 30 }, { projectId: this.workspace.currentProjectId });
            const msgs = res.messages || [];
            if (this.chatList) {
                this.chatList.setMessages(msgs, res.hasMore);
            }
            this.scrollToBottom();
        } catch (e) {
            this.log.error("Failed to refresh messages", e);
        }
    }

    _onChatEvent(payload) {
        if (!payload || payload.threadId !== this.workspace.currentThreadId) return;
        if (payload.type === 'member_changed' && payload.thread) {
            this._currentThread = payload.thread;
            this.infoPanel?.setThread(this._currentThread);
            this.renderHeader();
        } else if (payload.type === 'compactionStarted') {
            this.isCompacting = true;
            this.updateThinkingBubbleText(this.t('Compacting history…'), true);
            if (this.thinkingBubble) this.thinkingBubble.classList.add('visible');
        } else if (payload.type === 'compactionProgress') {
            this.isCompacting = true;
            const pctText = payload.pct ? ` (${payload.pct}%)` : '';
            this.updateThinkingBubbleText(this.t(`Compacting history${pctText}…`), true);
            if (this.thinkingBubble) this.thinkingBubble.classList.add('visible');
        } else if (payload.type === 'compactionDone') {
            this.isCompacting = false;
            if (this.isProcessing) {
                this.updateThinkingBubbleText(this.t('Working…'), true);
            } else {
                if (this.thinkingBubble) this.thinkingBubble.classList.remove('visible');
            }
        }
    }


    async jumpToMessage(messageId) {
        this.workspace._pendingMessageJump = null;
        if (this.chatList) {
            this.chatList._suppressScrollPin = true;
        }
        try {
            let el = this.messagesContainer.querySelector(`[data-message-id="${messageId}"]`);
            if (el) {
                this._highlightElement(el);
                return;
            }

            // If not found, load older messages up to 15 times to see if we can find it
            let attempts = 0;
            while (!el && this.chatList?._hasMore && attempts < 15) {
                await this.chatList._loadOlderMessages();
                // Allow dynamic child rendering and scroll anchor adjustments to fully settle
                await new Promise(resolve => setTimeout(resolve, 50));
                el = this.messagesContainer.querySelector(`[data-message-id="${messageId}"]`);
                attempts++;
            }

            if (el) {
                this._highlightElement(el);
            } else {
                this.appClient.notifier?.warn("Message is too far back in history to highlight.");
                this.searchOverlay?.close();
            }
        } finally {
            // Keep scroll-pin suppressed during rendering, scrolling, and transition settle time
            setTimeout(() => {
                if (this.chatList) {
                    this.chatList._suppressScrollPin = false;
                }
            }, 800);
        }
    }

    _highlightElement(el) {
        // Use 'instant' so scroll-pinning from setMessages / ResizeObserver can't cancel it.
        el.scrollIntoView({ behavior: 'instant', block: 'center' });
        // Add highlight after the scroll position is committed to the paint layer.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                el.classList.remove('search-highlight'); // reset in case it's already animating
                void el.offsetWidth;                     // force reflow to restart animation
                el.classList.add('search-highlight');
                setTimeout(() => el.classList.remove('search-highlight'), 3000);
            });
        });
    }

    _commitTurnArtifacts(state, { cancelled = false, metadata = null } = {}) {
        if (this.chatList) this.chatList.clearStream();

        if (cancelled) {
            for (const tool of this._currentTurnTools) {
                if (tool.status === 'running') {
                    tool.status = 'cancelled';
                    tool.result = 'Cancelled before completion.';
                }
            }
        }

        const finalContent = state?.streamBuffer || '';
        if (finalContent && this.chatList) {
            const assistantMetadata = { ...(metadata || {}) };
            if (this._currentTurnTools.length > 0) {
                assistantMetadata.tools = [
                    ...(Array.isArray(assistantMetadata.tools) ? assistantMetadata.tools : []),
                    ...this._currentTurnTools
                ];
            }
            if (this._pendingPresentations.length > 0) {
                assistantMetadata.presentations = [
                    ...(Array.isArray(assistantMetadata.presentations) ? assistantMetadata.presentations : []),
                    ...this._pendingPresentations
                ];
            }
            this.chatList.appendMessage({ role: 'assistant', content: finalContent, metadata: assistantMetadata });
        } else {
            if (this._currentTurnTools.length > 0 && this.chatList) {
                this.chatList.appendMessage({ role: 'tool_group', metadata: { tools: this._currentTurnTools } });
            }
            for (const p of this._pendingPresentations) {
                if (this.chatList) this.chatList.appendMessage({ role: 'presentation', metadata: p });
            }
        }

        if (state) state.streamBuffer = '';
    }

    _cleanupTurnState() {
        if (this.chatList) this.chatList.clearStream();
        this.setProcessing(false);
        if (this.currentTurnToolGroup?.container) this.currentTurnToolGroup.container.remove();
        this.currentTurnToolGroup = null;
        this.activeDirectTools.clear();
        this._pendingPresentations = [];
        this._currentTurnTools = [];
        if (this.liveArea) this.liveArea.innerHTML = '';
    }

    setProcessing(isProcessing) {
        this.isProcessing = isProcessing;
        if (this.thinkingBubble) {
            this.thinkingBubble.classList.toggle('visible', isProcessing || this.isCompacting);
            if (!isProcessing && !this.isCompacting) {
                this.updateThinkingBubbleText(this.t('Working…'), true);
            }
        }
        if (this.workspace.inputComponent && typeof this.workspace.inputComponent.setProcessingState === 'function') {
            this.workspace.inputComponent.setProcessingState(isProcessing);
        }
    }

    updateThinkingBubbleText(text, showDots = true) {
        if (!this.thinkingBubble) return;
        const dotsEl = this.thinkingBubble.querySelector('.ws-thinking-dots');
        const textEl = this.thinkingBubble.querySelector('.ws-thinking-text');
        if (dotsEl) dotsEl.style.display = showDots ? 'inline-flex' : 'none';
        if (textEl) textEl.textContent = text;
    }

    async getAllFilesFromDataTransfer(dataTransfer) {
        const files = [];
        const queue = [];

        if (dataTransfer.items) {
            for (let i = 0; i < dataTransfer.items.length; i++) {
                const item = dataTransfer.items[i];
                if (item.kind === 'file') {
                    const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
                    if (entry) {
                        queue.push(entry);
                    } else {
                        const file = item.getAsFile();
                        if (file) files.push(file);
                    }
                }
            }
        } else if (dataTransfer.files) {
            for (let i = 0; i < dataTransfer.files.length; i++) {
                files.push(dataTransfer.files[i]);
            }
        }

        if (queue.length > 0) {
            const readEntry = (entry) => {
                return new Promise((resolve) => {
                    if (entry.isFile) {
                        entry.file((file) => {
                            file.relativeFilePath = entry.fullPath.replace(/^\//, '');
                            files.push(file);
                            resolve();
                        }, () => resolve());
                    } else if (entry.isDirectory) {
                        const reader = entry.createReader();
                        const readAllEntries = () => {
                            reader.readEntries((entries) => {
                                if (entries.length === 0) {
                                    resolve();
                                } else {
                                    const promises = entries.map(e => readEntry(e));
                                    Promise.all(promises).then(() => readAllEntries()).catch(() => resolve());
                                }
                            }, () => resolve());
                        };
                        readAllEntries();
                    } else {
                        resolve();
                    }
                });
            };

            const promises = queue.map(entry => readEntry(entry));
            await Promise.all(promises);
        }

        return files;
    }


    async addMessageToUI(msg, skipScroll = false) {
        this._clearGreeting();
        if (this.chatList) {
            this.chatList.clearStream();
            this.chatList.appendMessage(msg);
        }
        if (!skipScroll) this.scrollToBottom();
    }

    detachMessages() {
        const dom = this.messagesInner;
        if (!dom) return null;
        const scrollTop = this.messagesContainer ? this.messagesContainer.scrollTop : 0;

        // Save ChatList state along with DOM snapshots
        const chatListState = this.chatList ? {
            messages: this.chatList._messages,
            hasMore: this.chatList._hasMore,
        } : null;

        const newInner = document.createElement('div');
        newInner.className = 'ws-messages-inner';
        if (this.messagesContainer) this.messagesContainer.replaceChild(newInner, this.messagesInner);
        this.messagesInner = newInner;

        if (this.chatList) {
            this.chatList.messagesInner = newInner;
            this.chatList._messages = [];
        }

        return { dom, scrollTop, chatListState };
    }

    attachMessages(domNode, scrollTop, chatListState) {
        this.isProcessing = false;
        this.isCompacting = false;
        if (this.thinkingBubble) {
            this.thinkingBubble.classList.remove('visible');
            this.updateThinkingBubbleText(this.t('Working…'), true);
        }
        if (!domNode || !this.messagesContainer) return;

        const threadId = this.workspace.currentThreadId;
        if (threadId) {
            this._currentThread = this.workspace.sidebarComponent?.allThreads?.find(t => t._id === threadId) || null;
            this.infoPanel?.setThread(this._currentThread);
            this.renderHeader();
        }

        if (this.messagesInner) this.messagesContainer.replaceChild(domNode, this.messagesInner);
        else this.messagesContainer.appendChild(domNode);
        this.messagesInner = domNode;

        if (this.chatList && chatListState) {
            this.chatList.messagesInner = domNode;
            this.chatList._messages = chatListState.messages;
            this.chatList._hasMore = chatListState.hasMore;
        }

        requestAnimationFrame(() => {
            if (this.messagesContainer) {
                this.messagesContainer.scrollTop = scrollTop || 0;
            }
            this.checkScrollPosition();
        });
    }

    applyLiveState(state) {
        this.setProcessing(state.isProcessing);
        if (state.isProcessing && state.streamBuffer && this.chatList) {
            this.chatList.updateStreamContent(marked.parse(state.streamBuffer));
            this.scrollToBottom();
        }
    }

    async markAsRead(threadId) {
        if (!threadId) return;
        try { await this.appClient.rpcClient.call('ThreadRpc.markAsRead', { threadId }, { projectId: this.workspace.currentProjectId }); } catch (e) { }
    }

    async handleLiveEvent(payload, state) {
        if (payload.agentName !== this.workspace.currentAgentName) return;
        this._clearGreeting();

        switch (payload.type) {
            case 'status':
                if (this.isVoiceActive) this.handleVoiceEvent(payload.status);
                this.setProcessing(payload.status === 'processing');
                break;
            case 'interrupt':
                if (this.audioClient) this.audioClient.interrupt();
                break;
            case 'message':
                let content = payload.message.content || "";
                const parts = content.split('---MESSAGE-BODY---');
                content = parts.length > 1 ? parts[1].trim() : content;
                if (content) this.addMessageToUI({ role: 'assistant', content, metadata: payload.message.metadata });
                this.currentTurnToolGroup = null;
                break;
            case 'cancellation_notice':
                state.cancellationNoticeShown = true;
                this.addMessageToUI(payload.message || {
                    role: 'system',
                    content: 'Stop requested. Completed tool work and partial output will be preserved when the current operation reaches a safe boundary.',
                    metadata: { reason: 'cancelled' }
                });
                break;
            case 'message_delta':
                if (this.chatList) {
                    this.chatList.updateStreamContent(marked.parse(state.streamBuffer));
                    this.scrollToBottom();
                }
                this.currentTurnToolGroup = null;
                break;
            case 'message_done': {
                await this.refreshMessages();
                if (this.chatList) this.chatList.clearStream();
                this._cleanupTurnState();
                this.scrollToBottom();
                break;
            }
            case 'error':
                if (payload.error === 'recursion_limit') {
                    await this.refreshMessages();
                } else if (payload.error === 'cancelled') {
                    await this.refreshMessages();
                    if (!state.cancellationNoticeShown) {
                        this.addMessageToUI({
                            role: 'system',
                            content: 'Response stopped. Completed tool work and partial output were preserved.',
                            metadata: { reason: 'cancelled' }
                        });
                    }
                    state.cancellationNoticeShown = false;
                } else if (payload.error !== 'cancelled') {
                    await this.refreshMessages();
                    this.addMessageToUI({ role: 'system', content: `Error: ${payload.error}` });
                }
                if (this.chatList) this.chatList.clearStream();
                this._cleanupTurnState();
                this.scrollToBottom();
                break;
            case 'tool_event':
                this.handleToolEvent(payload.event, payload.agentName, state);
                break;
            case 'voice_transcript':
                if (payload.isFinal) {
                    const prefix = payload.speaker === 'user' ? '<i class="bi bi-mic text-white-50 me-1" title="Spoken"></i> ' : '<i class="bi bi-volume-up text-muted me-1" title="Spoken"></i> ';
                    this.addMessageToUI({ role: payload.speaker === 'user' ? 'user' : 'assistant', content: prefix + payload.text, isEphemeral: true });
                }
                break;
        }
    }

    async handleToolEvent(event, agentName, state) {
        const { eventName, payload, jobId } = event;
        this._clearGreeting();

        if (eventName.startsWith('agentic.job.')) {
            let cardPromise = this.activeSkillCards.get(jobId);
            if (eventName === 'agentic.job.start' && !cardPromise) {
                if (this.chatList) this.chatList.appendMessage({ role: 'tool_progress', content: payload.goal, metadata: { jobId: jobId } });
                this.scrollToBottom();
                cardPromise = this.activeSkillCards.get(jobId);
            }
            if (cardPromise) {
                const card = await cardPromise;
                card.handleEvent(eventName, payload);
                if (eventName === 'agentic.job.complete' || eventName === 'agentic.job.fail' || eventName === 'agentic.job.cancel') {
                    this.runningJobIds.delete(jobId);
                    this.checkScrollPosition();
                }
                this.scrollToBottom();
            }
        }
        else if (eventName === 'agentic.output.present') {
            this._pendingPresentations.push({ ...payload, agentName: agentName || this.workspace.currentAgentName });
        }
        else if (eventName.startsWith('agentic.task.')) {
            if (payload.toolName === 'DelegateToAgent') {
                if (eventName === 'agentic.task.start') {
                    const wrapper = document.createElement('div');
                    wrapper.className = 'ws-msg-wrapper assistant w-100 justify-content-center';
                    if (this.liveArea) this.liveArea.appendChild(wrapper);
                    else this.messagesInner.appendChild(wrapper);
                    const delCard = await this.createManagedComponent('DelegationCard', wrapper, {
                        instanceId: `del-${payload.taskId}`, targetAgent: payload.params.agentName, prompt: payload.params.prompt
                    });
                    this.activeDelegationCards.set(payload.taskId, delCard);
                    this.scrollToBottom();
                } else if (this.activeDelegationCards.has(payload.taskId)) {
                    const card = this.activeDelegationCards.get(payload.taskId);
                    if (eventName === 'agentic.task.complete') card.updateStatus('complete', payload.result);
                    if (eventName === 'agentic.task.fail') card.updateStatus('fail', payload.error);
                    this.scrollToBottom();
                }
            }
            else if (jobId && this.activeSkillCards.has(jobId)) {
                const card = await this.activeSkillCards.get(jobId);
                card.handleEvent(eventName, payload);
                this.scrollToBottom();
            }
            else if (!jobId) {
                this.handleDirectToolEvent(eventName, payload, state);
            }
        }
        else if (eventName.startsWith('agentic.orchestrator.') || eventName.startsWith('agentic.computer_control.') || eventName.startsWith('terminal.')) {
            const id = payload?.skillJobId || jobId;
            const cardPromise = id ? this.activeSkillCards.get(id) : null;
            if (cardPromise) {
                const card = await cardPromise;
                card.handleEvent(eventName, payload);
                this.scrollToBottom();
            }
        }
    }

    handleDirectToolEvent(eventName, payload, state) {
        const { taskId, toolName, ui = {} } = payload;
        if (ui.hidden) return;

        if (eventName === 'agentic.task.start') {
            // Commit current stream segment to liveArea before starting the tool
            if (this.chatList && this.chatList._streamWrapper && state && state.streamBuffer) {
                const staticBubble = this.chatList._streamWrapper;
                staticBubble.classList.remove('streaming');

                // Detach from active stream tracking so clearStream() doesn't remove it
                this.chatList._streamWrapper = null;
                this.chatList._streamBubble = null;
                state.streamBuffer = '';
            }

            if (!this.currentTurnToolGroup) {
                const wrapper = document.createElement('div');
                wrapper.className = 'ws-msg-wrapper assistant w-100';
                if (this.liveArea) this.liveArea.appendChild(wrapper);
                else this.messagesInner.appendChild(wrapper);

                this.currentTurnToolGroup = new DirectToolGroup({
                    instanceId: `dtg-${Date.now()}-${Math.random().toString(36).slice(2)}`, appClient: this.appClient, workspace: this.workspace,
                    tools: [], isOpen: true, threadId: this.workspace.currentThreadId,
                });
                this.currentTurnToolGroup.render(wrapper);
                this.currentTurnToolGroup.container = wrapper;
                this.scrollToBottom();
            }
            const toolData = { taskId, toolName, ui, params: payload.params, status: 'running' };
            this._currentTurnTools.push(toolData);
            this.currentTurnToolGroup.addTool(toolData);
            this.activeDirectTools.set(taskId, this.currentTurnToolGroup);
            this.scrollToBottom();
        } else if (eventName === 'agentic.task.complete') {
            const group = this.activeDirectTools.get(taskId);
            if (group) {
                group.updateTool(toolName, taskId, 'completed', payload.result);
                const tool = this._currentTurnTools.find(t => t.taskId === taskId);
                if (tool) { tool.status = 'completed'; tool.result = payload.result; }
                this.activeDirectTools.delete(taskId);
                this.scrollToBottom();
            }
        } else if (eventName === 'agentic.task.fail') {
            const group = this.activeDirectTools.get(taskId);
            if (group) {
                group.updateTool(toolName, taskId, 'failed', payload.error);
                const tool = this._currentTurnTools.find(t => t.taskId === taskId);
                if (tool) { tool.status = 'failed'; tool.error = payload.error; }
                this.activeDirectTools.delete(taskId);
                this.scrollToBottom();
            }
        }
    }

    scrollToBottom(smooth = false) {
        if (!this.messagesContainer) return;
        const behavior = smooth ? 'smooth' : 'auto';
        requestAnimationFrame(() => {
            if (this.messagesContainer) {
                this.messagesContainer.scrollTo({ top: this.messagesContainer.scrollHeight, behavior });
            }
            // Double check after dynamic heights fully settle (ResizeObserver/async renders)
            setTimeout(() => {
                if (this.messagesContainer) {
                    this.messagesContainer.scrollTo({ top: this.messagesContainer.scrollHeight, behavior });
                    this._updateScrollBtn();
                }
            }, 60);
        });
    }

    scrollToActiveSkill() {
        if (this.runningJobIds.size > 0) {
            const firstJobId = this.runningJobIds.values().next().value;
            const el = this.container.querySelector(`#job-wrapper-${firstJobId}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    _updateScrollBtn() {
        if (!this.scrollBtn || !this.messagesContainer) return;
        const { scrollTop, scrollHeight, clientHeight } = this.messagesContainer;
        const atBottom = scrollHeight - scrollTop - clientHeight < 80;
        this.scrollBtn.classList.toggle('visible', !atBottom);
    }

    checkScrollPosition() {
        this._updateScrollBtn();
        if (this.runningJobIds.size === 0) {
            if (this.jumpBtn) this.jumpBtn.style.display = 'none';
            return;
        }
        const containerRect = this.messagesContainer.getBoundingClientRect();
        let anyVisible = false;
        for (const jobId of this.runningJobIds) {
            const el = this.container.querySelector(`#job-wrapper-${jobId}`);
            if (el) {
                const rect = el.getBoundingClientRect();
                if (rect.top >= containerRect.top && rect.bottom <= containerRect.bottom) {
                    anyVisible = true;
                    break;
                }
            }
        }
        if (this.jumpBtn) this.jumpBtn.style.display = anyVisible ? 'none' : 'block';
    }

    handleFilesPanelToggled({ open }) {
        if (!this.messagesContainer) return;
        const panelWidth = '320px';
        this.messagesContainer.style.transition = 'padding-right 0.25s ease';
        this.messagesContainer.style.paddingRight = open ? panelWidth : '0';
        const inputOverlay = this.container.closest('.ai-workspace-chat-container')?.parentElement?.querySelector('.ai-workspace-input-overlay');
        if (inputOverlay) {
            inputOverlay.style.transition = 'right 0.25s ease';
            inputOverlay.style.right = open ? panelWidth : '0';
        }
    }

    destroy() {
        this.appClient.events.server.off('agentResponse', this._boundHandleAgentResponse);
        this.appClient.events.server.off('chatEvent', this._boundHandleChatEvent);
        this.appClient.events.local.off('agentFilesPanelToggled', this._boundHandleFilesPanelToggled);
        this.appClient.events.local.off('TOGGLE_CHAT_SEARCH', this._boundToggleChatSearch);
        this.appClient.events.local.off('AGENT_AVATAR_UPDATED', this._boundHandleAgentAvatarUpdated);
        this.messagesContainer?.removeEventListener('scroll', this._boundScrollHandler);
        if (this.audioClient) this.audioClient.stop();
        if (this.previewModal) { this.previewModal.destroy(); this.previewModal = null; }
        if (this.filesPanel) { this.filesPanel.destroy(); this.filesPanel = null; }
        if (this.searchOverlay) { this.searchOverlay.destroy(); this.searchOverlay = null; }
        if (this.infoPanel) { this.infoPanel.destroy(); this.infoPanel = null; }
        if (this._contactPicker) { this._contactPicker.unmount(); this._contactPicker = null; }
        if (this.chatList) { this.chatList.destroy(); this.chatList = null; }
        super.destroy();
    }

    _handleAgentAvatarUpdated({ agentId, avatarUrl, name }) {
        if (this.agents) {
            const matchedAgent = this.agents.find(a => a._id === agentId || a.name === name);
            if (matchedAgent) {
                matchedAgent.avatarUrl = avatarUrl;
            }
        }
        if (this._currentThread) {
            const members = this._currentThread.members || [];
            const isGroup = this._currentThread.type === 'group' || this._currentThread.type === 'ai_human' || members.length > 2;
            const isAgent = !isGroup && (members.length <= 1 || members.some(m => m.participantType === 'agent'));
            if (isAgent) {
                const agentMember = members.find(m => m.participantType === 'agent');
                const agentName = this._currentThread.agentName || agentMember?.participantId;
                if (agentName === name) {
                    this.renderHeader();
                }
            }
            if (this.infoPanel && this.infoPanel.isOpen) {
                this.infoPanel.setThread(this._currentThread);
            }
        }
    }
}
